import { computed, ref } from 'vue'
import { ElMessage } from 'element-plus'
import {
  getEnterpriseInvoiceDownstreamAllocations,
  getEnterpriseInvoiceDownstreamTagPreferences,
  getEnterpriseInvoicePlan,
  validateEnterpriseInvoicePlan
} from '@/api/invoice'
import { groupCustomersByRegion } from '../utils'
import { hasBlockingInvoiceIssue } from '../rules.mjs'

export function useEnterpriseInvoicePlanState() {
  const loading = ref(false)
  const syncing = ref(false)
  const plan = ref(null)
  const customers = ref([])
  const regions = ref([])
  const chains = ref([])
  const ledgerItems = ref([])
  const validationIssues = ref([])
  const downstreamAllocations = ref([])
  const downstreamTagPreferences = ref([])
  let loadRequestSeq = 0

  const selectedPlanId = computed(() => plan.value?.id || null)
  const customersByRegion = computed(() => groupCustomersByRegion(customers.value, regions.value))
  const hasErrorIssues = computed(() => hasBlockingInvoiceIssue(validationIssues.value))

  function normalizeRegions(value = []) {
    return [...value].sort((left, right) => (left.displayOrder || 0) - (right.displayOrder || 0))
  }

  function normalizeCustomers(value = []) {
    return [...value].sort((left, right) => (left.displayOrder || 0) - (right.displayOrder || 0))
  }

  function normalizeChains(value = []) {
    return [...value].sort((left, right) => {
      const leftChainNo = Number(left.chainNo)
      const rightChainNo = Number(right.chainNo)
      if (Number.isFinite(leftChainNo) && Number.isFinite(rightChainNo)) {
        return leftChainNo - rightChainNo
      }
      return String(left.chainNo || '').localeCompare(String(right.chainNo || ''), 'zh-CN')
    })
  }

  function applySyncResult(sync) {
    if (!sync) return
    plan.value = sync.plan
    regions.value = normalizeRegions(sync.regions || [])
    customers.value = normalizeCustomers(sync.customers || [])
    chains.value = normalizeChains(sync.chains || [])
    ledgerItems.value = sync.ledgerItems
    validationIssues.value = sync.validationIssues
  }

  async function refreshDownstreamRules(planId = selectedPlanId.value) {
    if (!planId) {
      downstreamAllocations.value = []
      downstreamTagPreferences.value = []
      return
    }
    try {
      const allocations = await getEnterpriseInvoiceDownstreamAllocations(planId)
      downstreamAllocations.value = Array.isArray(allocations) ? allocations : []
    } catch (error) {
      console.error('Failed to load enterprise invoice downstream allocations', error)
      downstreamAllocations.value = []
      ElMessage.warning('指定下游配置加载失败')
    }
    try {
      const preferences = await getEnterpriseInvoiceDownstreamTagPreferences(planId)
      downstreamTagPreferences.value = Array.isArray(preferences) ? preferences : []
    } catch (error) {
      console.error('Failed to load enterprise invoice downstream tag preferences', error)
      downstreamTagPreferences.value = []
      ElMessage.warning('下游标签偏好加载失败')
    }
  }

  async function loadPlan(planId) {
    if (!planId) return
    const requestSeq = ++loadRequestSeq
    loading.value = true
    try {
      const sync = await getEnterpriseInvoicePlan(planId)
      if (requestSeq !== loadRequestSeq) return
      applySyncResult(sync)
      await refreshDownstreamRules(planId)
    } catch (error) {
      if (requestSeq !== loadRequestSeq) return
      console.error('Failed to load enterprise invoice plan', error)
      ElMessage.error('加载企营增方案失败')
    } finally {
      if (requestSeq === loadRequestSeq) {
        loading.value = false
      }
    }
  }

  async function runSyncAction(action, successMessage) {
    if (syncing.value) return null
    syncing.value = true
    try {
      const sync = await action()
      applySyncResult(sync)
      if (selectedPlanId.value) {
        await refreshDownstreamRules(selectedPlanId.value)
      }
      if (successMessage) {
        ElMessage.success(successMessage)
      }
      return sync
    } catch (error) {
      console.error('Enterprise invoice sync action failed', error)
      ElMessage.error(error?.message || '保存企营增方案失败')
      return null
    } finally {
      syncing.value = false
    }
  }

  // 校验接口返回的是 issue 列表（非 sync result），不能走 applySyncResult，否则会清空状态。
  // 这里只刷新 validationIssues，并复用 syncing 标志避免并发编辑。
  async function refreshIssues(planId, successMessage) {
    if (!planId) return
    if (syncing.value) return
    syncing.value = true
    try {
      const issues = await validateEnterpriseInvoicePlan(planId)
      validationIssues.value = Array.isArray(issues) ? issues : []
      if (successMessage) {
        ElMessage.success(successMessage)
      }
    } catch (error) {
      console.error('Failed to refresh enterprise invoice validation issues', error)
      ElMessage.error('校验失败')
    } finally {
      syncing.value = false
    }
  }

  return {
    loading,
    syncing,
    plan,
    customers,
    regions,
    chains,
    ledgerItems,
    validationIssues,
    downstreamAllocations,
    downstreamTagPreferences,
    selectedPlanId,
    customersByRegion,
    hasErrorIssues,
    applySyncResult,
    loadPlan,
    runSyncAction,
    refreshDownstreamRules,
    refreshIssues
  }
}
